import {FluxAvatar} from "@buildwithflux/avatar-react-mui";
import {getAccountMenuItems, getFluxMenuItems, signInUrl, signUpUrl} from "@buildwithflux/navigation";
import type {NavigationMenuItem, NavigationUser} from "@buildwithflux/navigation";
import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import Link from "@mui/material/Link";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {alpha} from "@mui/material/styles";
import type {Theme} from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router";

import FluxIcon from "../assets/flux_icon.svg";

import SubspaceDock from "./SubspaceDock";

const AVATAR_SIZE = 32;
const MENU_ICON_SIZE = 18;
const MENU_WIDTH = 320;

const iconSx = {fontSize: MENU_ICON_SIZE} as const;

const searchInputSx = (theme: Theme) => ({
    width: "200px",
    maxWidth: "200px",
    height: "32px",
    backgroundColor: theme.palette.grey[900],
    borderRadius: "4px",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: 14,
    letterSpacing: 0.15,
    color: "text.primary",
    "&:hover:not(.Mui-disabled):not(.Mui-focused)": {
        background: `linear-gradient(rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0.16)), ${theme.palette.grey[900]}`,
    },
    "&.Mui-focused": {
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    "& > .MuiSvgIcon-root": {
        width: "16px",
        height: "16px",
        marginRight: "4px",
    },
    "& input::placeholder": {
        color: alpha(theme.palette.common.white, 0.38),
        opacity: 1,
    },
});

const profileMenuAnchorOrigin = {
    vertical: "bottom" as const,
    horizontal: "right" as const,
};

const profileMenuTransformOrigin = {
    vertical: "top" as const,
    horizontal: "right" as const,
};

const ICON_MAP: {[key: string]: React.ComponentType<{sx?: object}>} = {
    Add: AddIcon,
    AddBox: AddBoxIcon,
    CloudUpload: CloudUploadIcon,
    CreditCard: CreditCardIcon,
    ExitToApp: ExitToAppIcon,
};

function renderMenuItemIcon(item: NavigationMenuItem) {
    if (item.icon === "Avatar") {
        return null;
    }
    const IconComponent = ICON_MAP[item.icon];
    if (!IconComponent) return null;
    return <IconComponent sx={iconSx} />;
}

interface HeaderProps {
    userInfo: NavigationUser | null;
    authResolved: boolean;
}

function ProfileMenu({
    userInfo,
    anchorEl,
    onClose,
}: {
    userInfo: NavigationUser;
    anchorEl: HTMLElement | null;
    onClose: () => void;
}) {
    const handleLogout = useCallback(() => {
        onClose();
        window.location.href = "/logout";
    }, [onClose]);

    const menuItems = useMemo(() => getAccountMenuItems(userInfo, handleLogout), [userInfo, handleLogout]);

    return (
        <Menu
            id="account-menu"
            anchorEl={anchorEl}
            anchorOrigin={profileMenuAnchorOrigin}
            transformOrigin={profileMenuTransformOrigin}
            open={Boolean(anchorEl)}
            onClose={onClose}
            slotProps={{paper: {sx: {width: MENU_WIDTH, mt: 0.5}}}}
        >
            {menuItems.map((item, idx) => {
                if (item.icon === "Avatar") {
                    return [
                        <MenuItem
                            key={item.id}
                            dense
                            component={Link}
                            href={item.href}
                            onClick={onClose}
                            underline="none"
                            color="inherit"
                        >
                            <ListItemIcon>
                                <FluxAvatar uid={userInfo.uid} size={MENU_ICON_SIZE} alt={userInfo.name} />
                            </ListItemIcon>
                            {item.label}
                        </MenuItem>,
                        item.dividerAfter ? <Divider key={`${item.id}-divider`} /> : null,
                    ];
                }

                const icon = renderMenuItemIcon(item);
                const handleClick = item.onClick
                    ? () => {
                          item.onClick?.();
                          onClose();
                      }
                    : onClose;

                return [
                    item.href ? (
                        <MenuItem
                            key={item.id}
                            dense
                            component={Link}
                            href={item.href}
                            onClick={handleClick}
                            underline="none"
                            color="inherit"
                        >
                            {icon && <ListItemIcon>{icon}</ListItemIcon>}
                            {item.label}
                        </MenuItem>
                    ) : (
                        <MenuItem key={item.id} dense onClick={handleClick}>
                            {icon && <ListItemIcon>{icon}</ListItemIcon>}
                            {item.label}
                        </MenuItem>
                    ),
                    item.dividerAfter ? <Divider key={`${item.id}-divider`} /> : null,
                    idx < menuItems.length - 1 && !item.dividerAfter ? null : null,
                ];
            })}
        </Menu>
    );
}

export default function Header({userInfo, authResolved}: HeaderProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
    const [fluxMenuAnchorEl, setFluxMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(location.search).get("q") ?? "");

    const profileMenuOpen = Boolean(profileAnchorEl);
    const fluxMenuOpen = Boolean(fluxMenuAnchorEl);

    const isAuthenticated = userInfo != null;

    const fluxMenuItems = useMemo(() => getFluxMenuItems(isAuthenticated), [isAuthenticated]);

    const handleProfileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setProfileAnchorEl(event.currentTarget);
    }, []);

    const handleProfileMenuClose = useCallback(() => {
        setProfileAnchorEl(null);
    }, []);

    const handleFluxMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setFluxMenuAnchorEl(event.currentTarget);
    }, []);

    const handleFluxMenuClose = useCallback(() => {
        setFluxMenuAnchorEl(null);
    }, []);

    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key !== "Enter") return;
            const value = (e.target as HTMLInputElement).value;
            if (value) {
                void navigate(`/search?q=${encodeURIComponent(value)}`);
            }
        },
        [navigate],
    );

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    useEffect(() => {
        if (typeof document === "undefined") return;
        if (!authResolved) return;
        document.documentElement.setAttribute("data-auth-state", isAuthenticated ? "authed" : "anon");
    }, [authResolved, isAuthenticated]);

    return (
        <AppBar color="inherit" position="fixed" elevation={0} sx={{bgcolor: "background.paper"}}>
            <Toolbar variant="dense" disableGutters sx={{px: "8px"}}>
                <Box
                    component="nav"
                    aria-label="Main navigation"
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {/* Left: logo + search */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            flexShrink: 1,
                            flexGrow: 1,
                            height: 48,
                        }}
                    >
                        <Button
                            id="nav-action-menu-button"
                            aria-label="Action menu"
                            title="Open actions menu"
                            aria-haspopup="true"
                            aria-expanded={fluxMenuOpen ? "true" : undefined}
                            color="inherit"
                            onClick={handleFluxMenuOpen}
                            size="small"
                            sx={{
                                padding: "5px 0 5px 5px",
                                verticalAlign: "middle",
                                margin: "4px 12px 4px 0",
                                minWidth: "49px",
                            }}
                        >
                            <img
                                src={FluxIcon}
                                aria-label="Home"
                                alt="Home"
                                width="24px"
                                height="30px"
                                draggable={false}
                            />
                            {fluxMenuOpen ? (
                                <ArrowDropUpIcon fontSize="small" />
                            ) : (
                                <ArrowDropDownIcon fontSize="small" />
                            )}
                        </Button>

                        <Menu
                            id="flux-action-menu"
                            anchorEl={fluxMenuAnchorEl}
                            open={fluxMenuOpen}
                            onClose={handleFluxMenuClose}
                            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                            transformOrigin={{vertical: "top", horizontal: "left"}}
                            slotProps={{paper: {sx: {mt: 0.5, minWidth: 220}}}}
                        >
                            {fluxMenuItems.map((item) => {
                                const icon = renderMenuItemIcon(item);
                                return [
                                    <MenuItem
                                        key={item.id}
                                        component="a"
                                        href={item.href}
                                        onClick={handleFluxMenuClose}
                                        dense
                                    >
                                        {icon && <ListItemIcon>{icon}</ListItemIcon>}
                                        {item.label}
                                        {item.external && (
                                            <LaunchIcon sx={{ml: 1, color: "text.secondary", fontSize: 14}} />
                                        )}
                                    </MenuItem>,
                                    item.dividerAfter ? <Divider key={`${item.id}-divider`} /> : null,
                                ];
                            })}
                        </Menu>

                        <InputBase
                            startAdornment={<SearchIcon sx={{color: "text.secondary"}} />}
                            placeholder="Search Flux..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            inputProps={{type: "search", "aria-label": "Search"}}
                            sx={searchInputSx}
                        />
                    </Box>

                    {/* Center spacer */}
                    <Box sx={{flexGrow: 1}} />

                    {/* Right: auth controls */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            height: 48,
                        }}
                    >
                        <SubspaceDock />
                        {/* Authed cluster */}
                        <span
                            data-auth-show="authed"
                            style={{
                                display: "none",
                                alignItems: "center",
                                verticalAlign: "middle",
                            }}
                        >
                            <Tooltip title={isAuthenticated ? "Open account menu" : ""}>
                                <Button
                                    id="nav-account-menu-button"
                                    aria-label="Account menu"
                                    aria-haspopup="true"
                                    aria-expanded={profileMenuOpen ? "true" : undefined}
                                    onClick={isAuthenticated ? handleProfileMenuOpen : undefined}
                                    color="inherit"
                                    size="small"
                                    disabled={!isAuthenticated}
                                    sx={{
                                        padding: "4px 0",
                                        verticalAlign: "middle",
                                        margin: "4px 0 4px 12px",
                                        "&.Mui-disabled": {opacity: 1},
                                    }}
                                >
                                    <FluxAvatar
                                        uid={userInfo?.uid ?? null}
                                        size={AVATAR_SIZE}
                                        alt={userInfo?.name ?? "User"}
                                    />
                                    {profileMenuOpen ? (
                                        <ArrowDropUpIcon fontSize="small" />
                                    ) : (
                                        <ArrowDropDownIcon fontSize="small" />
                                    )}
                                </Button>
                            </Tooltip>

                            {userInfo && (
                                <ProfileMenu
                                    userInfo={userInfo}
                                    anchorEl={profileAnchorEl}
                                    onClose={handleProfileMenuClose}
                                />
                            )}
                        </span>

                        {/* Anon cluster */}
                        <span
                            data-auth-show="anon"
                            style={{
                                display: "none",
                                alignItems: "center",
                                verticalAlign: "middle",
                            }}
                        >
                            <Button
                                variant="text"
                                color="primary"
                                href={signInUrl(location.pathname + location.search)}
                                size="small"
                                rel="nofollow"
                                sx={{
                                    padding: "4px 8px",
                                    verticalAlign: "middle",
                                    margin: "9px 6px",
                                    textTransform: "none",
                                    minWidth: "auto",
                                    fontSize: "0.875rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Log In
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                href={signUpUrl(location.pathname + location.search)}
                                size="small"
                                rel="nofollow"
                                sx={{
                                    padding: "4px 8px",
                                    verticalAlign: "middle",
                                    margin: "9px 0",
                                    textTransform: "none",
                                    minWidth: "auto",
                                    fontSize: "0.875rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Sign Up
                            </Button>
                        </span>
                    </Box>
                </Box>
            </Toolbar>
        </AppBar>
    );
}
